/**
 * IntegrationAccordion Component
 *
 * A reusable accordion component for the Integrations page that shows
 * status badges and handles expand/collapse state.
 */

import { useState, ReactNode } from 'react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface IntegrationAccordionProps {
  title: string
  description: string
  icon: ReactNode
  isEnabled: boolean
  isConfigured: boolean
  children: ReactNode
  defaultOpen?: boolean
  /** Additional badge to show (e.g., "Test Mode" / "Live Mode") */
  modeBadge?: ReactNode
}

/**
 * Get the status badge based on enabled/configured state
 */
function getStatusBadge(isEnabled: boolean, isConfigured: boolean) {
  if (isEnabled && isConfigured) {
    return (
      <span className="flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full">
        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
        Active
      </span>
    )
  }
  if (isEnabled && !isConfigured) {
    return (
      <span className="flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
        Not Configured
      </span>
    )
  }
  return (
    <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
      Disabled
    </span>
  )
}

export function IntegrationAccordion({
  title,
  description,
  icon,
  isEnabled,
  isConfigured,
  children,
  defaultOpen = false,
  modeBadge,
}: IntegrationAccordionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg bg-white overflow-hidden">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                {icon}
              </div>
              <div className="text-left">
                <h3 className="font-medium text-gray-900">{title}</h3>
                <p className="text-sm text-gray-500">{description}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {modeBadge}
              {getStatusBadge(isEnabled, isConfigured)}
              <ChevronDown
                className={cn(
                  'h-5 w-5 text-gray-400 transition-transform',
                  isOpen && 'rotate-180'
                )}
              />
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 pt-2 border-t">{children}</div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
